import React, { useState, useEffect } from 'react';
import { Link, useLocation } from "react-router-dom";
import logoLight from "../asset/images/logo_light.png";
import logoDark from "../asset/images/logo_dark.png";
import { motion, AnimatePresence } from "framer-motion";
import LanguageSelector from './languageSelector';
import { useTranslation } from 'react-i18next';
import { useTheme } from './ThemeProvider';
import { FiSun, FiMoon } from 'react-icons/fi';

const Nav = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const Links = [
    { name: 'home', link: '/' },
    { name: 'services', link: '/services' },
    { name: 'about', link: '/about' },
    { name: 'our_work', link: '/ourwork' },
    { name: 'contact_us', link: '/contactus' },
  ];

  // Force close mobile menu on navigation
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? 'py-3 bg-white/80 dark:bg-[#080808]/80 backdrop-blur-md border-b border-slate-200/50 dark:border-neutral-800/50 shadow-md dark:shadow-2xl'
          : 'py-5 bg-transparent border-b border-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2">
          <img
            src={theme === 'dark' ? logoLight : logoDark}
            alt="Softkel Logo"
            className="h-10 md:h-12 w-auto object-contain transition-all duration-300"
          />
        </Link>

        {/* Desktop Menu */}
        <nav className="hidden md:flex items-center gap-8">
          <ul className="flex items-center gap-6">
            {Links.map((link) => {
              const isActive = location.pathname === link.link;
              return (
                <li key={link.name} className="relative">
                  <Link
                    to={link.link}
                    className={`text-sm font-semibold transition-all duration-300 hover:text-blue-600 dark:hover:text-purple-400 ${
                      isActive
                        ? 'text-blue-600 dark:text-purple-400 font-bold'
                        : 'text-slate-600 dark:text-slate-300'
                    }`}
                  >
                    {t(link.name)}
                  </Link>
                  {isActive && (
                    <motion.span
                      layoutId="activeNavLine"
                      className="absolute -bottom-1.5 left-0 w-full h-[2px] bg-blue-600 dark:bg-purple-400 rounded-full"
                      transition={{ type: "spring", stiffness: 380, damping: 30 }}
                    />
                  )}
                </li>
              );
            })}
          </ul>

          <div className="h-6 w-[1px] bg-slate-200 dark:bg-neutral-800" />

          {/* Theme & i18n Selectors */}
          <div className="flex items-center gap-4">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-full bg-slate-100 hover:bg-slate-200 dark:bg-neutral-800 dark:hover:bg-neutral-700 text-slate-700 dark:text-slate-300 border border-slate-200/50 dark:border-neutral-700/50 transition-all"
              aria-label="Toggle Theme"
            >
              <motion.div
                initial={false}
                animate={{ rotate: theme === 'dark' ? 180 : 0 }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
              >
                {theme === 'dark' ? (
                  <FiSun className="w-4 h-4 text-amber-400" />
                ) : (
                  <FiMoon className="w-4 h-4 text-indigo-600" />
                )}
              </motion.div>
            </button>
            <LanguageSelector />
          </div>
        </nav>

        {/* Mobile Action Controls */}
        <div className="flex items-center gap-3 md:hidden">
          <button
            onClick={toggleTheme}
            className="p-1.5 rounded-full bg-slate-100 dark:bg-neutral-800 text-slate-700 dark:text-slate-300 border border-slate-200/50 dark:border-neutral-700/50"
            aria-label="Toggle Theme"
          >
            {theme === 'dark' ? (
              <FiSun className="w-4 h-4 text-amber-400" />
            ) : (
              <FiMoon className="w-4 h-4 text-indigo-600" />
            )}
          </button>
          <LanguageSelector />
          
          {/* Hamburger Menu Toggle Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="flex flex-col justify-center items-center w-8 h-8 rounded-full bg-slate-100 dark:bg-neutral-800 border border-slate-200/50 dark:border-neutral-700/50"
            aria-label="Toggle Mobile Menu"
          >
            <div className="w-4 h-4 flex flex-col justify-between items-center relative">
              <motion.span
                animate={mobileMenuOpen ? { rotate: 45, y: 6 } : { rotate: 0, y: 0 }}
                className="w-4 h-[2px] bg-slate-800 dark:bg-slate-200 rounded"
              />
              <motion.span
                animate={mobileMenuOpen ? { opacity: 0 } : { opacity: 1 }}
                className="w-4 h-[2px] bg-slate-800 dark:bg-slate-200 rounded"
              />
              <motion.span
                animate={mobileMenuOpen ? { rotate: -45, y: -6 } : { rotate: 0, y: 0 }}
                className="w-4 h-[2px] bg-slate-800 dark:bg-slate-200 rounded"
              />
            </div>
          </button>
        </div>
      </div>

      {/* Mobile Drawer Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="md:hidden w-full bg-white dark:bg-[#080808] border-b border-slate-200 dark:border-neutral-800 overflow-hidden"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              <ul className="flex flex-col gap-3">
                {Links.map((link) => {
                  const isActive = location.pathname === link.link;
                  return (
                    <li key={link.name}>
                      <Link
                        to={link.link}
                        className={`block py-2 text-md font-semibold rounded-lg px-3 transition-colors ${
                          isActive
                            ? 'bg-blue-50 text-blue-600 dark:bg-purple-950/20 dark:text-purple-400'
                            : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-neutral-900'
                        }`}
                      >
                        {t(link.name)}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Nav;
